import { boot } from '@dazn/bootstrap-lib';

type Language = boot.config.Language;

const isLanguageAllowed = (
    allowedLanguages: Language[],
    lang: Language | undefined,
): lang is Language => (!lang ? false : allowedLanguages.includes(lang));

const languageSegmentRegex = /^\/([a-z]{2})-[a-z]{2}\//i;
export const getLanguageFromURL = (pathname: string) => {
    const match = languageSegmentRegex.exec(pathname);
    if (match !== null) {
        const [, language] = match;
        return language.toLowerCase();
    }
    return null;
};

export const getLanguage = ({
    startupSupportedLanguages,
    startupLanguage,
    cachedLanguage,
    userSupportedLanguages,
    userProfileLanguage,
    pathname,
    skipUrlLanguageValidation,
    isUserLanguagePriority,
}: {
    startupSupportedLanguages: Language[];
    startupLanguage: Language;
    cachedLanguage: Language | null;
    userSupportedLanguages: Language[] | undefined;
    userProfileLanguage: Language | undefined;
    pathname: string;
    skipUrlLanguageValidation?: boolean;
    isUserLanguagePriority?: boolean;
}) => {
    const isProfileLangAllowed = isLanguageAllowed(
        userSupportedLanguages ?? [],
        userProfileLanguage,
    );
    if (isUserLanguagePriority && isProfileLangAllowed) {
        return userProfileLanguage;
    }

    const urlLanguage = getLanguageFromURL(pathname);
    if (urlLanguage) {
        if (skipUrlLanguageValidation) {
            return urlLanguage;
        }

        const supportedLangs =
            userSupportedLanguages && userSupportedLanguages.length > 0
                ? userSupportedLanguages
                : startupSupportedLanguages;
        const isURLLanguageAllowed = isLanguageAllowed(
            supportedLangs,
            urlLanguage,
        );
        if (isURLLanguageAllowed) {
            return urlLanguage;
        }
    }

    if (cachedLanguage) {
        if (isLanguageAllowed(userSupportedLanguages ?? [], cachedLanguage)) {
            return cachedLanguage;
        }
        if (isProfileLangAllowed) {
            return userProfileLanguage;
        }
        if (isLanguageAllowed(startupSupportedLanguages, cachedLanguage)) {
            return cachedLanguage;
        }
    }

    return isProfileLangAllowed ? userProfileLanguage : startupLanguage;
};
